// @refresh reset — dev-only: force remount so board nodes hot-reload on edit
import { useMemo } from "react";
import { type Node, type Edge, type NodeTypes } from "@xyflow/react";
import { BoardCanvas } from "./BoardCanvas";
import { SectionBox, SectionGrid } from "./SectionBox";
import { DocumentFrame } from "./DocumentFrame";
import { DeckFrame } from "./DeckFrame";
import { airDuctLaneNodeTypes, buildUseCases, PAD, LABEL_BLOCK } from "./airduct/lanes";
import { flowNodeTypes, buildToday, buildWithUs, CONTENT_W } from "./airduct/flow";
import { AirDuctApp } from "./airduct/app";
import { AirDuctPricingSheet } from "./airduct/calculator";
import { AirDuctScopeEmail } from "./airduct/email";

// ─────────────────────────────────────────────────────────────────────────────
// THE AIR DUCT BOARD — engagement surface for the Texas home-services company
// run by Daniel Adam (air-duct cleaning, dryer vents, insulation), introduced
// by Matan Amoyal. Sources: meetings 89 + 90 in Neon, 23 July 2026.
//
// ✅ The name is CONFIRMED as of 2026-07-27, read off their live site: they
// trade as FRESH AIR DUCT CLEANING at bestairducts.com. Asset filenames point
// to an umbrella brand "Fresh Air Home Services". Seven Texas markets, ~78 city
// pages, 3,570 Google reviews, and Workiz + CallRail both visible in the page
// source — independent confirmation of the stack they described. The slug
// `air-duct-tx` stays as the internal key. The REGISTERED legal entity is still
// worth confirming with Matan before it goes on a contract.
//
// THREE ROWS. Restructured 7 Aug 2026 — this board is the PILOT for a standard
// that rolls to every project board:
//   ROW 1 · THE CORE — ONE section, exactly THREE documents, always first and
//           always in this order: THE PROJECT (where we stand), THE PRODUCT
//           (what we are building), THE ARCHITECTURE (how it is built). Both
//           the agent hydration reading list and Erez's at-a-glance view.
//           Nothing else is allowed in this box.
//           Revised 7 Aug: the first cut was WHO/WHERE/HOW, which folded
//           product and architecture together. They are separate documents now
//           because WHAT we build and HOW we build it change at different rates.
//   ROW 2 · THE DIAGRAMS — today, the same operation with us in it, and why.
//           Side by side on their own white sheets so they compare.
//   ROW 3 · THE WORK PRODUCT — the record, the demo, the plan, the commercials.
//           Was FOURTEEN sections; consolidated to four. Nothing was deleted —
//           see the map in the ShowNode docblock for where every piece went.
//
// DELETED and not commented out: the three Nano Banana renders, airduct/maps.tsx
// (process flow, business map, tally, architecture), airduct/system.tsx
// (blueprint, pain table, screens, scope gaps), and the Decisions cards. All in
// git. The discovery deck was removed in that pass too and has been RESTORED —
// it is a different artifact from the scoping deck: that one argues what we
// would build, this one is what we heard.
//
// The diagrams are React Flow nodes on the board's OWN canvas, in the LANES
// language (skill://diagrams §5). Never nest a <ReactFlow> inside a board node —
// the outer instance drops to zero nodes and the board renders blank silently.
//
// Owner agent: Hank.
// ─────────────────────────────────────────────────────────────────────────────

// Board chrome now reads in the Notion palette too (Erez, 2026-07-28) so the
// captions sit with the documents rather than against them: warm ink #37352F,
// the softer secondary, and Notion's own sans stack.
const T = { bg: "#EFEFEF", dots: "#DCDCDC", ink: "#37352F", ink2: "#787774" };
const R2 = "https://pub-f9d4dc80715b4656bb344d288227078e.r2.dev/pitch-doc/consultancy/air-duct-tx";
const DEMO = `${R2}/demo`;

// The discovery readout still exists at `${R2}/discovery` (film, 11 slides, PDF).
// It came off the board 2026-07-28 because Erez will make that argument himself
// in the room and wants the film to be the DEMO and nothing else. Put it back
// as its own box if that changes.

// One document height across every board (Erez, 7 Aug). 1180 is the value Ruby
// set on /everything-420-board and Erez called the right height. If a document
// does not fit, SHORTEN THE DOCUMENT, never lengthen the frame — a page that
// needs a taller box is a page too long to sit on a board.
const DOC_H = 1180;
const FONT = 'ui-sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';

function Caption({ title, body }: { title: string; body: string }) {
  return (
    <div style={{ fontFamily: FONT, maxWidth: 1500, display: "flex", flexDirection: "column", gap: 6 }}>
      <div style={{ fontSize: 30, fontWeight: 700, color: T.ink, letterSpacing: "-0.022em" }}>{title}</div>
      <div style={{ fontSize: 16, color: T.ink2, lineHeight: 1.5 }}>{body}</div>
    </div>
  );
}

/** A horizontal group inside a section. Sections must read WIDER THAN TALL, so
 *  many items go in rows of two or three rather than one long column. */
function Row({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", gap: 34, alignItems: "flex-start" }}>{children}</div>;
}

/** ROW 1 — THE CORE. The board-standard opening section (Erez + Jarvis,
 *  7 Aug 2026), piloted here before it rolls to every board.
 *
 *  ONE section, exactly THREE documents, always first, always this order:
 *    1 · THE PROJECT      — where we stand. The project summary, as an article.
 *    2 · THE PRODUCT      — WHAT we are building. Capabilities and phases.
 *    3 · THE ARCHITECTURE — HOW it is built. Tailormind for THIS business,
 *                           carrying the CHANGELOG and the ATLAS REVIEWED TO
 *                           watermark.
 *
 *  These three are simultaneously the agent hydration reading list and Erez's
 *  at-a-glance view, which is exactly why they are pinned and why nothing else
 *  is allowed into this box. Everything else on the board is WORK PRODUCT and
 *  sits below.
 *
 *  Product and architecture are deliberately SEPARATE documents. Folding them
 *  into one (the first cut did) hides the fact that they change at different
 *  rates and are argued by different people: the product moves when a
 *  capability ships, the architecture when the system's shape changes.
 */
function RecordNode() {
  return (
    <SectionGrid width={3400} gap={190}>
      {/* THE CORE — the same first section on every project board, in the same
          order: where we stand, what we are building, how it is built. These
          three are what an agent HYDRATES on and what it keeps current; every
          other section on this board is work product and sits underneath.

          Changed 7 Aug 2026 (Erez): was WHO/WHERE/HOW, which folded product and
          architecture into one document. Product and architecture are now two,
          because WHAT we are building and HOW it is built change at different
          rates and are argued by different people. "The business" was the old
          WHO and has moved down into The record, where the rest of the
          discovery evidence already lives. */}
      <SectionBox title="The core" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexDirection: "column", gap: 26, alignItems: "flex-start" }}>
          <Caption
            title="Where we stand, what we are building, how it is built"
            body="The three documents everything else springs from. An agent reads these to pick the thread back up, and updates them as the work moves — the project summary continuously, the product as capabilities go live, the architecture with a changelog entry Atlas reviews. Read left to right."
          />
          <Row>
            <DocumentFrame slug="kb-doc/projects/air-duct-tx" label="1 · THE PROJECT — where we stand" width={900} height={DOC_H} />
            <DocumentFrame slug="kb-doc/consultancy/air-duct-tx/product" label="2 · THE PRODUCT — what we are building" width={900} height={DOC_H} />
            <DocumentFrame slug="kb-doc/consultancy/air-duct-tx/architecture" label="3 · THE ARCHITECTURE — how it is built" width={900} height={DOC_H} />
          </Row>
        </div>
      </SectionBox>
    </SectionGrid>
  );
}
/** ROW 3 — THE WORK PRODUCT.
 *
 *  CONSOLIDATED 7 Aug 2026 (Erez authorised, this board only). It had grown by
 *  accretion to FOURTEEN sections: five were all commercials, three were all the
 *  demo. Nothing was deleted — every document, film, prototype and calculator
 *  that was on the board is still on the board. What changed is that related
 *  work now shares ONE gray box instead of having one each, and the three
 *  orientation documents were pulled out into the "Start here" section above.
 *
 *  Where each of the fourteen went:
 *    The business      → Start here (WHO)
 *    The documents     → split: the project page → Start here (WHERE);
 *                        discovery record + prep questionnaire → The record
 *    John's answers    → The record
 *    The demo (film)   → The demo
 *    The demo system   → The demo
 *    The app           → The demo
 *    The pitch deck    → The plan
 *    The phases        → The plan
 *    The next five     → The plan
 *    Scope & price     → The commercials
 *    The work order    → The commercials
 *    The model         → The commercials
 *    Scope confirmation→ The commercials
 *    The pricing sheet → The commercials
 *  (The architecture doc in Start here is NEW — it did not exist before.)
 *
 *  Sections read WIDER THAN TALL: many items go in <Row> groups of two or three,
 *  never one long column.
 *
 *  ⚠️ Put notes like this ABOVE the return, never as a brace-star JSX comment on
 *  the line before the root element. Inside `return ( … )` the root element must
 *  be the only expression, so a leading brace-comment is a SYNTAX ERROR — and it
 *  is one `tsc -p tsconfig.json` does not see, because the build typechecks
 *  `tsconfig.app.json`. It cost a failed deploy that also broke the next agent's
 *  build. Verify with the command the build actually runs: `npm run build`.
 */
// Every section is ONE internal row, so each box is ~940 tall and as wide as its
// contents. Stacking a second row inside a box (film+doc, then the app) made the
// demo and the commercials read TALLER THAN WIDE on the live board, which is the
// one shape the craft law forbids. Measured from the live render, not guessed.
const SHOW_ROW_W = 18200;

function ShowNode() {
  return (
    <SectionGrid width={SHOW_ROW_W} gap={190}>
      {/* THE RECORD — what came out of the room, unprocessed. The source every
          other section on this board is derived from. */}
      <SectionBox title="The record" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexDirection: "column", gap: 26, alignItems: "flex-start" }}>
          <Caption
            title="What they actually said, and what came back in writing"
            body="Who they are, the full discovery record read back from all 497 transcript segments, the questionnaire we sent them to prepare, and John's written answers — ten for ten yes, with four qualifiers that moved the scope. These are the source; everything else here is derived from them."
          />
          <Row>
            <DocumentFrame slug="kb-doc/consultancy/air-duct-tx/the-business" label="The business — who they are" width={900} height={DOC_H} />
            <DocumentFrame slug="kb-doc/consultancy/air-duct-tx/discovery" label="The discovery record" width={900} height={DOC_H} />
            <DocumentFrame slug="kb-doc/consultancy/air-duct-tx/prep-questionnaire" label="Prep questionnaire" width={900} height={DOC_H} />
            <DocumentFrame slug="kb-doc/consultancy/air-duct-tx/johns-answers" label="John's answers" width={900} height={DOC_H} />
          </Row>
        </div>
      </SectionBox>

      {/* THE DEMO — was three sections (The demo, The demo system, The app).
          They are one argument: the thing we built, the film of it, and the
          front-end prototype that preceded it. */}
      <SectionBox title="The demo" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexDirection: "column", gap: 26, alignItems: "flex-start" }}>
          <Caption
            title="Their own company, running — https://167-233-212-150.sslip.io"
            body="Built for Daniel's question, do you have anything you can show us. Seven Texas markets, 5,798 jobs, 14,765 calls, twelve months that actually compute. Ask answers from rows and shows the working; four automations emit real PDFs on their own schedule. Every screen stamped as sample data, connected to nothing of theirs. The film is a narrated walkthrough rendered from the real component, so it can be re-rendered rather than re-recorded; the prototype below it is the front-end that came first."
          />
          <Row>
            <video
              src={`${DEMO}/film.mp4`}
              poster={`${DEMO}/poster.jpg`}
              controls
              preload="none"
              className="nowheel nodrag nopan"
              onPointerDown={(e) => e.stopPropagation()}
              style={{ pointerEvents: "auto", width: 1560, borderRadius: 3, border: "1px solid #E9E9E7", background: "#000", display: "block" }}
            />
            <DocumentFrame slug="kb-doc/consultancy/air-duct-tx/demo-system" label="The demo system" width={900} height={DOC_H} />
            <AirDuctApp />
          </Row>
        </div>
      </SectionBox>

      {/* THE PLAN — the argument, the sequence, and what comes after. */}
      <SectionBox title="The plan" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexDirection: "column", gap: 26, alignItems: "flex-start" }}>
          <Caption
            title="The argument, the order, and what comes after the demo"
            body="The scoping deck attempts the whole solution before asking them anything and ends on the four questions that genuinely stopped us — that is the one you present live. The phased plan carries the column that matters: how many hours of THEIR team each phase costs, roughly 45 to 50 across twelve weeks. The next five is the scope this build does not carry, each with its root cause and a proposed mechanism."
          />
          <Row>
            <DeckFrame slug="pitch-doc/consultancy/air-duct/scoping" label="Air Duct TX · scoping attempt · internal" width={1560} height={940} />
            <DocumentFrame slug="kb-doc/consultancy/air-duct-tx/phases" label="The phased plan" width={900} height={DOC_H} />
            <DocumentFrame slug="kb-doc/consultancy/air-duct-tx/next-five" label="The next five — additional scope" width={900} height={DOC_H} />
          </Row>
        </div>
      </SectionBox>

      {/* THE COMMERCIALS — was FIVE sections. They are one thing: how we scope
          it, how we price it, and what we send. In the order it happens:
          confirm the scope → price it → write the model down → send the order.

          DIVERGENCE from "a section is a KB page, a presentation, or a diagram":
          the scope-confirmation email and the pricing sheet are bespoke
          interactive components, not documents. They are kept deliberately —
          Erez asked for the sheet as a live calculator ("I want to see it as a
          sheet but I don't want to go out to a sheet") and later said it should
          become a reusable product. Deleting them to satisfy the section rule
          would destroy work the rule was never aimed at. Flagged rather than
          silently absorbed. */}
      <SectionBox title="The commercials" layout="custom">
        <div style={{ marginTop: 0, display: "flex", flexDirection: "column", gap: 26, alignItems: "flex-start" }}>
          <Caption
            title="How we scope it, how we price it, and what goes out"
            body="In the order it happens. Confirm the scope in writing before quoting anything — that step is Erez-locked and the email carries none of our own user estimates, because putting them in would hand us back our own guesses. Then the formula and the number it produces, the written model behind the rubric, and the work order itself: $40,000 and twelve weeks, drafted and NOT SENT, awaiting Erez's ratification of the number."
          />
          <Row>
            <DocumentFrame slug="kb-doc/consultancy/air-duct-tx/scope-and-price" label="Scope & price" width={900} height={DOC_H} />
            <DocumentFrame slug="kb-doc/consultancy/air-duct-tx/feature-pricing" label="The pricing model" width={900} height={DOC_H} />
            <DocumentFrame slug="kb-doc/consultancy/air-duct-tx/work-order" label="Work order — draft, not sent" width={900} height={DOC_H} />
            <AirDuctScopeEmail />
            <AirDuctPricingSheet />
          </Row>
        </div>
      </SectionBox>
    </SectionGrid>
  );
}
const nodeTypes: NodeTypes = {
  ...(airDuctLaneNodeTypes as unknown as NodeTypes),
  ...(flowNodeTypes as unknown as NodeTypes),
  record: RecordNode as unknown as NodeTypes[string],
  show: ShowNode as unknown as NodeTypes[string],
};

// ONE container round all three diagrams (Erez, 2026-07-26). They are one
// argument — how it runs today, the same thing with us in it, and why — so they
// get one gray box and one pill, and each keeps its own title inside. Three
// separate frames read as three unrelated objects.
//
// SIDE BY SIDE, not stacked (Erez, 2026-07-27). Stacked, the three read as one
// long page. In a row they read as three comparable things — which is the point,
// since 1 and 2 are the same operation before and after. Each gets its OWN white
// sheet with real space between, all inside the one gray container.
//
// Wide is fine here BECAUSE the row still fits above the ~0.15 fitView zoom where
// onlyRenderVisibleElements starts culling edges (skill://diagrams §4) — checked
// on the live board, not assumed.
const PANEL_PAD = 130;   // white around each diagram
const COL_GAP = 240;     // space between the white sheets
const W_TODAY = CONTENT_W, W_WITHUS = CONTENT_W, W_WHY = 2280;
const H_TODAY = 820, H_WITHUS = 1350, H_WHY = 1560;
const ROW_H = Math.max(H_TODAY, H_WITHUS, H_WHY);   // sheets align as a row

const X_TODAY = 0;
const X_WITHUS = X_TODAY + W_TODAY + 2 * PANEL_PAD + COL_GAP;
const X_WHY = X_WITHUS + W_WITHUS + 2 * PANEL_PAD + COL_GAP;
const ROW_RIGHT = X_WHY + W_WHY + PANEL_PAD;        // right edge of the last sheet

function build(): { nodes: Node[]; edges: Edge[] } {
  // frame = false: the container + the sheets below supply the chrome
  const a = buildToday(X_TODAY, 0, false);
  const b = buildWithUs(X_WITHUS, 0, false);
  const c = buildUseCases(X_WHY, 0, false);

  const sheet = (id: string, x: number, w: number): Node => ({
    id, type: "adpanel", position: { x: x - PANEL_PAD, y: -PANEL_PAD },
    draggable: false, selectable: false, zIndex: 1,
    data: { w: w + 2 * PANEL_PAD, h: ROW_H + 2 * PANEL_PAD },
  });

  // THREE ROWS (Erez, 2026-07-27), in the order the story is told:
  //   1 the record — documents + the pitch deck
  //   2 the diagrams
  //   3 what we would show them — the discovery deck + the app
  // The diagrams keep content at y=0, so their gray frame top is at -FRAME_TOP.
  const FRAME_TOP = PANEL_PAD + PAD + LABEL_BLOCK;
  const ROW_GAP = 420;
  // Row 1 is now ONE section of three 1040px frames plus its caption and the
  // SectionBox padding. Overstating this only opens dead space above the
  // diagrams, so it is measured rather than left at the old fourteen-section value.
  const H_ROW1 = 1620;
  const Y_ROW1 = -FRAME_TOP - ROW_GAP - H_ROW1;
  const Y_ROW3 = ROW_H + PANEL_PAD + PAD + ROW_GAP;
  const ROW_X = -(PANEL_PAD + PAD);
  return {
    nodes: [
      { id: "diagrams-frame", type: "fframe",
        position: { x: -(PANEL_PAD + PAD), y: -(PANEL_PAD + PAD + LABEL_BLOCK) },
        draggable: false, selectable: false, zIndex: 0,
        data: { label: "The diagrams", bare: true,
          w: ROW_RIGHT + PANEL_PAD + 2 * PAD,
          h: ROW_H + 2 * PANEL_PAD + 2 * PAD + LABEL_BLOCK } },
      sheet("sheet-1", X_TODAY, W_TODAY),
      sheet("sheet-2", X_WITHUS, W_WITHUS),
      sheet("sheet-3", X_WHY, W_WHY),
      ...a.nodes,
      ...b.nodes,
      ...c.nodes,
      // ⚠️ INTERACTIVE NODES NEED THREE THINGS, not one (2026-07-27).
      // A `nowheel nodrag nopan` class on the inner component is necessary but
      // NOT sufficient. React Flow gives a `selectable: false` node wrapper
      // `pointer-events: none`, and `.react-flow__pane` sits at z-index 1 while
      // `.react-flow__nodes` is z-index auto — so the canvas pane paints OVER
      // every node and eats the click. The component renders perfectly, the
      // state logic is perfectly correct, and nothing responds to a mouse.
      // The full recipe: nowheel/nodrag/nopan on the component + stopPropagation
      // + `style: { pointerEvents: "auto" }` and a zIndex ABOVE the pane here.
      // These two rows carry every clickable thing on the board — the pricing
      // sheet, the copy button, the app's nav, the deck arrows, the video.
      { id: "record", type: "record", position: { x: ROW_X, y: Y_ROW1 }, draggable: false, selectable: false,
        zIndex: 10, style: { pointerEvents: "auto" }, data: {} },
      { id: "show", type: "show", position: { x: ROW_X, y: Y_ROW3 }, draggable: false, selectable: false,
        zIndex: 10, style: { pointerEvents: "auto" }, data: {} },
    ],
    edges: [...a.edges, ...b.edges, ...c.edges],
  };
}

export function AirDuctBoardPage() {
  const { nodes, edges } = useMemo(() => build(), []);
  return (
    <div style={{ position: "absolute", inset: 0 }}>
      <BoardCanvas nodes={nodes} edges={edges} nodeTypes={nodeTypes}
        fitViewPadding={0.06} />
    </div>
  );
}
